/**
 * AudioReactiveCore — what the music is doing, frame by frame, for visuals.
 *
 * Two halves with one contract between them:
 *
 *   Analyzer  — stateless spectral analysis of one window of samples:
 *               total level, 32 band levels (dB), spectral centroid.
 *               Runs wherever the samples are: in the AudioWorklet
 *               (public/js/audio-reactive/worklet.js), on the main thread
 *               over an AnalyserNode (fallback), in a Worker over decoded
 *               PCM, in node tests. Same numbers everywhere.
 *   Tracker   — stateful, time-stamped: per-band noise floor and peak,
 *               normalised levels, onsets (kick / snare / hat), tempo and
 *               beat phase, and the smoothed "drivers" a scene reads.
 *
 * Why not the AnalyserNode byte spectrum averaged per band (what
 * audio-features.js did until 2026-09): those values are dB mapped onto a
 * fixed −100…−30 dB window, so a track 16× quieter moved the picture by
 * 14 %; the onset threshold was relative only, so room hiss "hit" more often
 * than music; the tempo never let go, so pulses went on in silence.
 * Measured on real songs — docs/why/primitives/audio-reactive.md.
 *
 * The rules that fix it:
 *   - a level means "where is this band between its own recent peak and the
 *     background": 1 at the peak, 0 about DISPLAY_RANGE_DB below it or at the
 *     noise floor, whichever is higher — loud or quiet input, the same swing;
 *   - a band that does not rise above its noise floor by NOISE_MARGIN_SIGMA
 *     noise deviations reads exactly 0 — hiss does not move the picture;
 *   - an onset is a rise of the gated band level (dB) against its own recent
 *     median + MAD, so it is loud-and-sudden, never "a bit above average";
 *   - the beat is predicted from the tempo and phase of the onset curve and
 *     stops when the onsets stop.
 *
 * Every time constant is in seconds, not in frames: the worklet analyses
 * every ~11 ms, the fallback on every animation frame; both must look alike.
 *
 * ES5, no DOM. Exposed as self.AudioReactiveCore / module.exports.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.AudioReactiveCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this), function () {
    'use strict';

    var BANDS = 32;
    var F_LO = 30;
    var F_HI = 16000;
    /* Macro groups by band centre frequency. The low group reaches 250 Hz on
       purpose: a laptop speaker (the usual source when the input is a mic)
       reproduces nothing under ~180 Hz, but the "punch" of a kick is still
       there between 180 and 250. */
    var GROUP_EDGES = [30, 250, 3000, 16000];
    var GROUPS = 3;

    var LN10_10 = 10 / Math.LN10;
    function db(p) { return p > 1e-20 ? LN10_10 * Math.log(p) : -200; }
    function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
    function smoothstep(a, b, x) { var t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
    /* One-pole smoothing coefficient for a step of dt seconds and time constant tau. */
    function k(dt, tau) { return tau > 0 ? 1 - Math.exp(-dt / tau) : 1; }

    /* ═══════════════════ Real FFT (power spectrum) ═══════════════════ */

    /* Radix-2 complex FFT of n/2 points over the packed real input, then the
       standard split into the n/2+1 bins of the real transform. */
    function RealFFT(n) {
        var m = n >> 1, bits = 0, i, b;
        while ((1 << bits) < m) bits++;
        this.n = n; this.m = m;
        this.re = new Float64Array(m); this.im = new Float64Array(m);
        this.rev = new Uint32Array(m);
        for (i = 0; i < m; i++) {
            var r = 0, x = i;
            for (b = 0; b < bits; b++) { r = (r << 1) | (x & 1); x >>= 1; }
            this.rev[i] = r;
        }
        this.cm = new Float64Array(m >> 1); this.sm = new Float64Array(m >> 1);
        for (i = 0; i < (m >> 1); i++) { this.cm[i] = Math.cos(2 * Math.PI * i / m); this.sm[i] = -Math.sin(2 * Math.PI * i / m); }
        this.cn = new Float64Array(m + 1); this.sn = new Float64Array(m + 1);
        for (i = 0; i <= m; i++) { this.cn[i] = Math.cos(2 * Math.PI * i / n); this.sn[i] = -Math.sin(2 * Math.PI * i / n); }
    }
    /* x: n windowed samples. out: n/2+1 values of |X[k]|². */
    RealFFT.prototype.power = function (x, out) {
        var m = this.m, re = this.re, im = this.im, rev = this.rev, cm = this.cm, sm = this.sm;
        var i, size, half, step, start, j;
        for (i = 0; i < m; i++) { j = rev[i]; re[j] = x[2 * i]; im[j] = x[2 * i + 1]; }
        for (size = 2; size <= m; size <<= 1) {
            half = size >> 1; step = m / size;
            for (start = 0; start < m; start += size) {
                for (j = 0; j < half; j++) {
                    var tw = j * step, wr = cm[tw], wi = sm[tw];
                    var a = start + j, bb = a + half;
                    var tr = re[bb] * wr - im[bb] * wi, ti = re[bb] * wi + im[bb] * wr;
                    re[bb] = re[a] - tr; im[bb] = im[a] - ti;
                    re[a] += tr; im[a] += ti;
                }
            }
        }
        for (i = 0; i <= m; i++) {
            var k0 = i === m ? 0 : i, km = i === 0 ? 0 : m - i;
            var zr = re[k0], zi = im[k0], cr = re[km], ci = -im[km];
            var er = (zr + cr) * 0.5, ei = (zi + ci) * 0.5;
            var orr = (zr - cr) * 0.5, oi = (zi - ci) * 0.5;
            var wr2 = this.cn[i], wi2 = this.sn[i];
            /* X = E + W^i · (−i·O) ; −i·O = (oi, −orr) */
            var xr = er + (oi * wr2 + orr * wi2), xi = ei + (oi * wi2 - orr * wr2);
            out[i] = xr * xr + xi * xi;
        }
    };

    /* ═══════════════════ Band layout ═══════════════════ */

    /* 32 bands from 30 Hz to 16 kHz (or 0.46·sr): logarithmic, but never
       narrower than one FFT bin — at 44.1 kHz / 2048 the first four bands are
       ~21 Hz wide, from ~100 Hz on each band is ~20 % wider than the last. */
    function bandEdges(sr, fftSize) {
        var hi = Math.min(F_HI, sr * 0.46), minW = sr / fftSize;
        function build(r) {
            var e = [F_LO];
            for (var i = 0; i < BANDS; i++) e.push(e[i] + Math.max(minW, e[i] * (r - 1)));
            return e;
        }
        var lo = 1.0001, up = 2.0;
        for (var it = 0; it < 60; it++) {
            var mid = (lo + up) / 2;
            if (build(mid)[BANDS] > hi) up = mid; else lo = mid;
        }
        var edges = build(lo);
        edges[BANDS] = hi;
        return edges;
    }

    /* ═══════════════════ Analyzer ═══════════════════ */

    function Analyzer(sampleRate, opts) {
        opts = opts || {};
        var sr = sampleRate;
        this.sampleRate = sr;
        this.fftSize = opts.fftSize || (sr > 64000 ? 4096 : (sr < 32000 ? 1024 : 2048));
        this.hop = opts.hop || (sr > 64000 ? 1024 : (sr < 32000 ? 256 : 512));
        var n = this.fftSize, i;
        this.fft = new RealFFT(n);
        this.win = new Float64Array(n);
        var s2 = 0;
        for (i = 0; i < n; i++) { this.win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / n); s2 += this.win[i] * this.win[i]; }
        /* |X|² · norm summed over a band = mean square of that band's content
           (a full-scale sine reads −3 dB, like its RMS). */
        this.norm = 2 / (n * s2);
        this.frame = new Float64Array(n);
        this.spec = new Float64Array(n / 2 + 1);
        this.edges = bandEdges(sr, n);
        this.centers = new Float32Array(BANDS);
        /* Fractional bin weights per band: rectangular band, partial edge bins. */
        var binHz = sr / n;
        this.bandLo = new Int32Array(BANDS); this.bandHi = new Int32Array(BANDS);
        this.bandW = [];
        for (var bnd = 0; bnd < BANDS; bnd++) {
            var fa = this.edges[bnd], fb = this.edges[bnd + 1];
            this.centers[bnd] = Math.sqrt(fa * fb);
            var ka = fa / binHz, kb = fb / binHz;
            var i0 = Math.max(1, Math.floor(ka)), i1 = Math.min(n / 2, Math.ceil(kb));
            var w = new Float64Array(i1 - i0 + 1);
            for (i = i0; i <= i1; i++) {
                /* bin i covers [i − 0.5, i + 0.5] */
                var ov = Math.min(i + 0.5, kb) - Math.max(i - 0.5, ka);
                w[i - i0] = ov > 0 ? Math.min(1, ov) : 0;
            }
            this.bandLo[bnd] = i0; this.bandHi[bnd] = i1; this.bandW.push(w);
        }
        this.bandPow = new Float64Array(BANDS);
        this.bandDb = new Float32Array(BANDS);
        this.totalDb = -200;
        this.centroidHz = 0;
    }

    /* samples: the newest fftSize samples, oldest first (plain array/typed array).
       If `ring` form is used, `pos` is the index of the oldest sample. */
    Analyzer.prototype.analyze = function (samples, pos) {
        var n = this.fftSize, f = this.frame, w = this.win, i;
        if (pos === undefined) {
            for (i = 0; i < n; i++) f[i] = samples[i] * w[i];
        } else {
            var len = samples.length, p = pos;
            for (i = 0; i < n; i++) { f[i] = samples[p] * w[i]; p++; if (p === len) p = 0; }
        }
        this.fft.power(f, this.spec);
        var spec = this.spec, norm = this.norm, tot = 0, cen = 0;
        for (var b = 0; b < BANDS; b++) {
            var wts = this.bandW[b], i0 = this.bandLo[b], acc = 0;
            for (i = 0; i < wts.length; i++) acc += spec[i0 + i] * wts[i];
            acc *= norm;
            this.bandPow[b] = acc;
            this.bandDb[b] = db(acc);
            tot += acc; cen += acc * this.centers[b];
        }
        this.totalDb = db(tot);
        this.centroidHz = tot > 1e-20 ? cen / tot : 0;
        return this;
    };

    /* The frame the Tracker consumes, as one flat array (what the worklet posts):
       [t, totalDb, centroidHz, bandDb × 32] */
    var FRAME_LEN = 3 + BANDS;
    Analyzer.prototype.writeFrame = function (t, out, off) {
        off = off || 0;
        out[off] = t; out[off + 1] = this.totalDb; out[off + 2] = this.centroidHz;
        for (var b = 0; b < BANDS; b++) out[off + 3 + b] = this.bandDb[b];
        return out;
    };

    /* ═══════════════════ Tracker ═══════════════════ */

    /* Tuned on the corpus (tests/js/audio-reactive-core.test.mjs) and on the
       rendered bench (tests/playwright/audio-visualizer-reactivity-bench.mjs);
       the measurements behind each value are in docs/why/primitives/audio-reactive.md. */
    var P = {
        /* level normalisation */
        SMOOTH_TAU: 0.035,          /* band value the levels are read from */
        /* band value the BACKGROUND is learnt from: noise in a band one FFT bin
           wide wanders +-4 dB over 35 ms and +-1.5 dB over 300 ms, so its low
           percentile sits next to its mean instead of 5-8 dB under it */
        BG_SMOOTH_TAU: 0.3,
        TOP_TAU: 20,                /* memory of "the loud parts": about one song section */
        TOP_Q: 0.97,                /* loud parts = the top 3 % of that memory */
        FLOOR_TAU: 20,              /* memory of the background: a steady tone becomes background after ~1 min */
        /* The whole signal in digital silence (a paused player, a mic warming
           up) is not a background, and for WARMUP_S after it the analysis
           window is still half zeros (a -88 dB "floor" that is not one): no
           background is learnt then. A single band silent between notes is
           different - its background really is silence. */
        SILENCE_DB: -120,
        WARMUP_S: 0.3,
        FLOOR_Q: 0.05,
        ANCHOR_EVERY_S: 0.05,
        /* A level is 1.0 at the loud parts and 0 this many dB below them:
           a single band swings widely, the sum of all bands much less. */
        RANGE_DB: [24, 22, 24],     /* band, group, total */
        /* a band this much weaker than the whole sound is leakage, not content */
        CROSS_DB: [-42, -24],
        /* A band must stand this far above its background to count at all:
           noise in a band one FFT bin wide wanders ±4 dB, in a band of 120
           bins ±0.5 dB (measured on room noise, docs/why/primitives/audio-reactive.md). */
        MARGIN_DB_1BIN: 8, MARGIN_DB_MIN: 3,
        /* onsets */
        ONSET_LAG_S: 0.02,
        ONSET_WIN_S: 0.6,
        ONSET_K_MAD: 2.5,
        ONSET_MIN_DB: [2.0, 1.6, 1.4],      /* average rise per band of the group, dB */
        ONSET_MIN_GAP_S: [0.10, 0.08, 0.06],
        /* a low-band onset is a kick only if the low rise dominates the mid rise
           (a snare body sits at 150-250 Hz too) */
        KICK_DOMINANCE: 1.0,
        /* envelopes */
        LEVEL_ATTACK: 0.012, LEVEL_RELEASE: 0.16,
        BAND_ATTACK: 0.010, BAND_RELEASE: 0.12,
        PULSE_TAU: [0.16, 0.17, 0.08],      /* kick, snare, hat */
        BEAT_TAU: 0.22,
        PRESENCE_ATTACK: 0.08, PRESENCE_RELEASE: 1.2,
        PRESENCE_DB: [3.5, 8],              /* sound over summed band floors: 0 below, 1 above */
        PRESENCE_EVENT_DB: [0, 6],          /* the k-th loudest band over its floor + margin */
        INTENSITY_TAU: 3.0,
        BRIGHT_TAU: 0.25,
        /* flows: integrated motion, "seconds of travel" per second */
        FLOW_IDLE: 0.18,
        /* tempo */
        GRID_HZ: 100,
        TEMPO_WIN_S: 8.0,
        TEMPO_EVERY_S: 0.25,
        TEMPO_WARMUP_S: 1.5,
        BPM_MIN: 60, BPM_MAX: 200, BPM_PRIOR: 120, BPM_PRIOR_OCT: 0.9,
        /* periodicity of the onset curve needed to start pulsing on the beat, and
           to keep doing it: speech peaks at 0.13, songs sit at 0.2-0.8 */
        LOCK_CONF: 0.18, UNLOCK_CONF: 0.10,
        /* ...and for this many tempo estimates in a row with the same tempo:
           syllables look periodic for a second or two, a song for minutes */
        LOCK_EVIDENCE: 6,
        LOCK_GAP_BEATS: 3.0,
        LOCK_GAP_MIN_S: 2.0
    };

    /* Running percentiles of one dB series with exponential forgetting:
       a 1-dB histogram where each new value weighs e^(t/τ) more than the
       one τ seconds before it. O(1) to add, one scan to read. */
    var H_LO = -140, H_N = 150;
    function Anchors(tau, empty) {
        this.tau = tau;
        this.empty = empty;          /* what the percentiles read before anything was learnt */
        this.c = new Float64Array(H_N);
        this.total = 0; this.w = 1;
    }
    Anchors.prototype.clear = function () { this.c.fill(0); this.total = 0; this.w = 1; };
    Anchors.prototype.add = function (x, dt) {
        this.w *= Math.exp(dt / this.tau);
        if (this.w > 1e30) {
            for (var j = 0; j < H_N; j++) this.c[j] /= this.w;
            this.total /= this.w; this.w = 1;
        }
        var i = Math.floor(x - H_LO);
        if (!(i >= 0)) i = 0; else if (i >= H_N) i = H_N - 1;
        this.c[i] += this.w; this.total += this.w;
    };
    Anchors.prototype.quantile = function (p) {
        var c = this.c, i, acc = 0, ci, target;
        if (!(this.total > 0)) return this.empty;
        if (p <= 0.5) {
            target = p * this.total;
            for (i = 0; i < H_N; i++) { ci = c[i]; if (acc + ci >= target && ci > 0) return H_LO + i + (target - acc) / ci; acc += ci; }
            return H_LO + H_N;
        }
        target = (1 - p) * this.total;
        for (i = H_N - 1; i >= 0; i--) { ci = c[i]; if (acc + ci >= target && ci > 0) return H_LO + i + 1 - (target - acc) / ci; acc += ci; }
        return H_LO;
    };

    /* One dB series (a band, a group, the total). Three separate questions:
         rel   - where is it between its loud parts and RANGE dB below them;
         bg    - does it stand above its own background at all (silence -> 0);
         cross - is it content, or leakage far under the rest of the sound.
       level = rel * bg * cross. */
    /* floorPrior: the background before any is learnt. A played file sits on
       digital silence, so -140 dB and the first note is heard at once; a
       microphone's room is unknown, so +10 dB (nothing counts as sound) until
       the first 0.3 s of it have been learnt — else the room flashes on start. */
    function Series(marginDb, rangeDb, floorPrior) {
        this.margin = marginDb;
        this.range = rangeDb;
        this.topH = new Anchors(P.TOP_TAU, -140);
        this.floorH = new Anchors(P.FLOOR_TAU, floorPrior);
        this.reset();
    }
    Series.prototype.reset = function () {
        this.sm = -200; this.smB = -200; this.floor = this.floorH.empty; this.top = -140; this.warm = P.WARMUP_S;
        this.rel = 0; this.bg = 0; this.norm = 0; this.n = 0;
        this.topH.clear(); this.floorH.clear();
    };
    Series.prototype.push = function (x, dt, readAnchors, rangeScale, allSilent) {
        var silent = x < P.SILENCE_DB;
        if (allSilent) this.warm = P.WARMUP_S;
        /* coming out of digital silence: start from the value, not from -140 dB */
        var wasSilent = this.sm < P.SILENCE_DB;
        this.sm = wasSilent && !silent ? x : this.sm + (x - this.sm) * k(dt, P.SMOOTH_TAU);
        if (this.sm < -150) this.sm = x;
        this.topH.add(this.sm, dt);
        if (this.warm > 0) {
            this.warm -= dt;
            this.smB = this.sm;
        } else {
            this.smB += (x - this.smB) * k(dt, P.BG_SMOOTH_TAU);
            this.floorH.add(this.smB, dt);
        }
        this.n++;
        if (readAnchors || this.n < 8) {
            this.floor = this.floorH.quantile(P.FLOOR_Q);
            this.top = this.topH.quantile(P.TOP_Q);
        }
        var top = this.sm > this.top ? this.sm : this.top;
        var range = this.range * rangeScale;
        this.rel = clamp01((this.sm - (top - range)) / range);
        this.bg = smoothstep(this.margin, 2 * this.margin, this.sm - this.floor);
        return this.rel * this.bg;
    };

    /* opts.background: 'silence' (a played file) or 'learn' (a microphone, the default) */
    function Tracker(sampleRate, opts) {
        opts = opts || {};
        var an = opts.analyzer || new Analyzer(sampleRate, opts);
        var prior = opts.background === 'silence' ? -140 : 10;
        this.centers = an.centers;
        this.groupOf = new Int8Array(BANDS);
        this.groupCount = new Int32Array(GROUPS);
        this.bandSeries = [];
        var g, b, i;
        for (b = 0; b < BANDS; b++) {
            var c = an.centers[b];
            g = 0;
            while (g < GROUPS - 1 && c >= GROUP_EDGES[g + 1]) g++;
            this.groupOf[b] = g; this.groupCount[g]++;
            var bins = 0, w = an.bandW[b];
            for (i = 0; i < w.length; i++) bins += w[i];
            this.bandSeries.push(new Series(Math.max(P.MARGIN_DB_MIN, P.MARGIN_DB_1BIN * Math.pow(Math.max(1, bins), -0.25)), P.RANGE_DB[0], prior));
        }
        this.groupFirst = new Int32Array(GROUPS);
        for (g = GROUPS - 1, b = BANDS - 1; b >= 0; b--) this.groupFirst[this.groupOf[b]] = b;
        this.groupSeries = [];
        var gBins = [0, 0, 0], allBins = 0;
        for (b = 0; b < BANDS; b++) {
            var wb = an.bandW[b], sb = 0;
            for (i = 0; i < wb.length; i++) sb += wb[i];
            gBins[this.groupOf[b]] += sb; allBins += sb;
        }
        function marginFor(bins) { return Math.max(P.MARGIN_DB_MIN, P.MARGIN_DB_1BIN * Math.pow(Math.max(1, bins), -0.25)); }
        for (g = 0; g < GROUPS; g++) this.groupSeries.push(new Series(marginFor(gBins[g]), P.RANGE_DB[1], prior));
        this.groupSeries.push(new Series(marginFor(allBins), P.RANGE_DB[2], prior));
        this.sensitivity = 1;
        this.reset();
    }

    Tracker.prototype.reset = function () {
        var nb = BANDS, b;
        this.t = -1;
        this.t0 = undefined;
        this.n = 0;
        this.nextAnchorAt = 0;
        for (b = 0; b < nb; b++) this.bandSeries[b].reset();
        for (b = 0; b <= GROUPS; b++) this.groupSeries[b].reset();
        this.gated = new Float64Array(nb).fill(-140);
        this.excess = new Float64Array(nb);
        /* ring of gated band dB for the onset lag */
        this.hist = [];
        this.histT = [];
        /* onset detection per group */
        this.odf = [[], [], []]; this.odfT = [[], [], []];
        this.lastOnset = [-1e9, -1e9, -1e9];
        this.onsetScale = [3, 3, 3];
        this.onsetEvent = [0, 0, 0];      /* strength of an onset found on this step, else 0 */
        this.onsetEventT = [0, 0, 0];
        this.prevOdf = [0, 0, 0]; this.prevPrevOdf = [0, 0, 0]; this.prevT = [0, 0, 0];
        this.onsetCount = 0;
        /* tempo */
        this.grid = new Float32Array(Math.ceil(P.TEMPO_WIN_S * P.GRID_HZ));
        this.gridPos = 0; this.gridT = -1; this.gridFill = 0;
        this.nextTempoAt = 0;
        this.bpm = 0; this.period = 0; this.conf = 0;
        this.pendingBpm = 0; this.pendingN = 0;
        this.nextBeat = 0; this.lastBeat = -1e9; this.locked = false;
        this.lockEvidence = 0; this.lastEstBpm = 0;
        this.beatEvent = 0; this.phaseMiss = 0;
        this.presence = 0;
        /* drivers */
        this.d = {
            level: 0, bass: 0, mid: 0, high: 0,
            kick: 0, snare: 0, hat: 0, onset: 0,
            beat: 0, beatPhase: 0, beatLock: 0, bpm: 0,
            presence: 0, intensity: 0, bright: 0,
            flow: 0, flowBass: 0, flowHigh: 0,
            spectrum: new Float32Array(BANDS)
        };
    };

    /* 0..1: does the sound in bands [b0, b1) stand above the background of
       those bands? Energy over summed band floors: a whistle (one band) and a
       full mix count alike, room noise sits 2-3 dB over its own floors. */
    Tracker.prototype.bandPresence = function (b0, b1, kth) {
        var e = 0, f = 0, b, i;
        var top = EV;                     /* the kth largest excess, kept sorted descending */
        for (i = 0; i < kth; i++) top[i] = -1e9;
        for (b = b0; b < b1; b++) {
            var se = this.bandSeries[b];
            e += Math.pow(10, se.smB / 10);
            f += Math.pow(10, se.floor / 10);
            var ex = se.sm - se.floor - se.margin;
            if (ex > top[kth - 1]) {
                for (i = kth - 1; i > 0 && top[i - 1] < ex; i--) top[i] = top[i - 1];
                top[i] = ex;
            }
        }
        /* energy: a whistle, a hum, a chord standing over the room; events:
           hits standing out of their own bands even over a steady pad */
        var energy = smoothstep(P.PRESENCE_DB[0], P.PRESENCE_DB[1], db(e) - db(f));
        var events = smoothstep(P.PRESENCE_EVENT_DB[0], P.PRESENCE_EVENT_DB[1], top[kth - 1]);
        return energy > events ? energy : events;
    };

    Tracker.prototype.setSensitivity = function (s) {
        this.sensitivity = Math.max(0.2, Math.min(3, +s || 1));
    };

    function median(arr, tmp) {
        var n = arr.length, i;
        tmp.length = n;
        for (i = 0; i < n; i++) tmp[i] = arr[i];
        tmp.sort(function (a, b) { return a - b; });
        return n ? (n & 1 ? tmp[n >> 1] : 0.5 * (tmp[(n >> 1) - 1] + tmp[n >> 1])) : 0;
    }
    var TMP = [], EV = [0, 0, 0, 0];
    var PULSES = ['kick', 'snare', 'hat'];

    /* One analysed frame in, all state advanced. frame: [t, totalDb, centroidHz, bandDb…] */
    Tracker.prototype.step = function (frame, off) {
        off = off || 0;
        var t = frame[off], i, b, g;
        var dt = this.t < 0 ? 0.0116 : t - this.t;
        if (!(dt > 0)) return this.d;            /* duplicate or out of order: ignore */
        if (dt > 0.25) dt = 0.25;                 /* a gap (tab hidden): do not jump the flows */
        this.t = t; this.n++;
        var readAnchors = t >= this.nextAnchorAt;
        if (readAnchors) this.nextAnchorAt = t + P.ANCHOR_EVERY_S;
        /* Sensitivity widens (or narrows) the dB span a level travels from 0
           to 1 below the loud parts: at x2 sounds ~1.7x as many dB quieter
           still move the picture, at x0.5 only the loudest moments do. It also
           scales the strength of hits, and never touches the silence gate. */
        var rs = Math.pow(this.sensitivity, 0.8);
        var allSilent = frame[off + 1] < P.SILENCE_DB;
        /* ---- per band: anchors, levels, gated dB for onsets ---- */
        var gPow = [0, 0, 0], totPow = 0, se;
        for (b = 0; b < BANDS; b++) {
            var x = frame[off + 3 + b];
            se = this.bandSeries[b];
            se.push(x, dt, readAnchors, rs, allSilent);
            var gate = se.floor + se.margin;
            this.gated[b] = x > gate ? x : gate;
            this.excess[b] = se.sm - gate;
            var pw = Math.pow(10, x / 10);
            gPow[this.groupOf[b]] += pw; totPow += pw;
        }
        /* Presence of a group (and of the whole sound) is read from its BANDS:
           in music a band stands 10-25 dB over its own background, while the
           sum of all bands only 4-6 dB over the sum of theirs - a sum judged
           by itself mistakes a steady song for its own background. */
        var presTarget = this.bandPresence(0, BANDS, 3);
        this.presence = presTarget;
        var tot = this.groupSeries[GROUPS];
        tot.push(db(totPow), dt, readAnchors, rs, allSilent);
        tot.norm = tot.rel * presTarget;
        for (g = 0; g < GROUPS; g++) {
            se = this.groupSeries[g];
            se.push(db(gPow[g]), dt, readAnchors, rs, allSilent);
            se.norm = se.rel * this.bandPresence(this.groupFirst[g], this.groupFirst[g] + this.groupCount[g], 2) *
                smoothstep(P.CROSS_DB[0], P.CROSS_DB[1], se.top - tot.top);
        }
        for (b = 0; b < BANDS; b++) {
            se = this.bandSeries[b];
            /* a band counts if it stands over its own background, or if it is
               among the loudest content anyway (a steady tone is not noise) */
            var own = Math.max(se.bg, smoothstep(-18, -6, se.top - tot.top));
            se.norm = se.rel * own * presTarget * smoothstep(P.CROSS_DB[0], P.CROSS_DB[1], se.top - tot.top);
        }
        /* ---- onsets: SuperFlux-style rise of the gated band dB ---- */
        this.hist.push(Float64Array.from(this.gated));
        this.histT.push(t);
        while (this.histT.length > 2 && t - this.histT[1] >= P.ONSET_LAG_S) { this.hist.shift(); this.histT.shift(); }
        var ref = this.hist[0];
        var odfNow = [0, 0, 0];
        if (this.hist.length > 1) {
            for (b = 0; b < BANDS; b++) {
                var r = ref[b];
                if (b > 0 && ref[b - 1] > r) r = ref[b - 1];
                if (b < BANDS - 1 && ref[b + 1] > r) r = ref[b + 1];
                var rise = this.gated[b] - r;
                if (rise > 0) odfNow[this.groupOf[b]] += rise;
            }
        }
        this.onsetEvent[0] = this.onsetEvent[1] = this.onsetEvent[2] = 0;
        var candRel = [0, 0, 0];
        for (g = 0; g < GROUPS; g++) candRel[g] = this.prevOdf[g] / Math.max(1, this.onsetScale[g]);
        for (g = 0; g < GROUPS; g++) {
            var v = odfNow[g] / this.groupCount[g];
            var od = this.odf[g], odT = this.odfT[g];
            /* Peak = the previous value, higher than its neighbours. */
            var cand = this.prevOdf[g], candT = this.prevT[g];
            if (od.length > 3 && cand > this.prevPrevOdf[g] && cand >= v) {
                var med = median(od, TMP);
                for (i = 0; i < od.length; i++) TMP[i] = Math.abs(od[i] - med);
                TMP.sort(function (a, c) { return a - c; });
                var mad = TMP[TMP.length >> 1];
                var thr = med + Math.max(P.ONSET_MIN_DB[g], P.ONSET_K_MAD * mad * 1.4826);
                if (cand >= thr && candT - this.lastOnset[g] >= P.ONSET_MIN_GAP_S[g]) {
                    this.lastOnset[g] = candT;
                    this.onsetScale[g] += (cand - this.onsetScale[g]) * 0.25;
                    var prom = 1 - Math.exp(-1.6 * cand / Math.max(1, this.onsetScale[g]));
                    var s = clamp01(prom * (0.45 + 0.55 * this.groupSeries[g].norm) * Math.sqrt(this.sensitivity));
                    /* a broadband hit (snare) lifts the low bands too: that is not a kick */
                    if (g !== 0 || candRel[0] >= P.KICK_DOMINANCE * candRel[1]) {
                        this.onsetEvent[g] = s > 0.02 ? s : 0.02;
                        this.onsetEventT[g] = candT;
                        this.onsetCount++;
                    }
                }
            }
            od.push(v); odT.push(t);
            while (odT.length && t - odT[0] > P.ONSET_WIN_S) { od.shift(); odT.shift(); }
            this.prevPrevOdf[g] = this.prevOdf[g]; this.prevOdf[g] = v; this.prevT[g] = t;
        }
        /* ---- tempo grid: combined onset curve on a fixed 100 Hz grid ---- */
        var comb = odfNow[0] / this.groupCount[0] + 0.7 * odfNow[1] / this.groupCount[1] + 0.35 * odfNow[2] / this.groupCount[2];
        /* while the background is still being learnt every rise looks big:
           the tempo does not listen to the first seconds */
        if (this.t0 === undefined) this.t0 = t;
        this.pushGrid(t, t - this.t0 < P.TEMPO_WARMUP_S ? 0 : comb);
        if (t >= this.nextTempoAt) { this.nextTempoAt = t + P.TEMPO_EVERY_S; this.estimateTempo(t); }
        this.advanceBeat(t, this.d.presence);
        /* ---- drivers ---- */
        var d = this.d, gs = this.groupSeries;
        var lv = gs[GROUPS].norm;
        d.level += (lv - d.level) * k(dt, lv > d.level ? P.LEVEL_ATTACK : P.LEVEL_RELEASE);
        d.bass += (gs[0].norm - d.bass) * k(dt, gs[0].norm > d.bass ? P.LEVEL_ATTACK : P.LEVEL_RELEASE);
        d.mid += (gs[1].norm - d.mid) * k(dt, gs[1].norm > d.mid ? P.LEVEL_ATTACK : P.LEVEL_RELEASE);
        d.high += (gs[2].norm - d.high) * k(dt, gs[2].norm > d.high ? P.LEVEL_ATTACK : P.LEVEL_RELEASE);
        for (b = 0; b < BANDS; b++) {
            var sb = d.spectrum[b], nb = this.bandSeries[b].norm;
            d.spectrum[b] = sb + (nb - sb) * k(dt, nb > sb ? P.BAND_ATTACK : P.BAND_RELEASE);
        }
        var any = 0;
        for (g = 0; g < GROUPS; g++) {
            var key = PULSES[g], cur = d[key] * Math.exp(-dt / P.PULSE_TAU[g]);
            if (this.onsetEvent[g] > cur) cur = this.onsetEvent[g];
            d[key] = cur;
            if (this.onsetEvent[g] > any) any = this.onsetEvent[g];
        }
        var on = d.onset * Math.exp(-dt / 0.12);
        d.onset = any > on ? any : on;
        var bt = d.beat * Math.exp(-dt / P.BEAT_TAU);
        if (this.beatEvent > bt) bt = this.beatEvent;
        d.beat = bt;
        d.beatLock += ((this.locked ? 1 : 0) - d.beatLock) * k(dt, 0.4);
        d.bpm = this.locked ? this.bpm : 0;
        d.beatPhase = this.locked && this.period > 0 ? clamp01(1 - (this.nextBeat - t) / this.period) : 0;
        d.presence += (presTarget - d.presence) * k(dt, presTarget > d.presence ? P.PRESENCE_ATTACK : P.PRESENCE_RELEASE);
        d.intensity += (d.level * d.presence - d.intensity) * k(dt, P.INTENSITY_TAU);
        var cHz = frame[off + 2];
        var brightTarget = cHz > 0 && presTarget > 0.2 ? clamp01((Math.log(cHz) - Math.log(400)) / (Math.log(5000) - Math.log(400))) : d.bright;
        d.bright += (brightTarget - d.bright) * k(dt, P.BRIGHT_TAU);
        /* Flows integrate speed, so a change of speed never jumps the picture. */
        d.flow += dt * (P.FLOW_IDLE + 0.9 * d.level + 0.6 * d.kick);
        d.flowBass += dt * (P.FLOW_IDLE + 1.1 * d.bass + 0.5 * d.kick);
        d.flowHigh += dt * (P.FLOW_IDLE + 1.1 * d.high + 0.4 * d.hat);
        return d;
    };

    Tracker.prototype.pushGrid = function (t, v) {
        var step = 1 / P.GRID_HZ, len = this.grid.length;
        if (this.gridT < 0) this.gridT = t;
        /* fill every grid cell up to t; the cell containing t takes the max */
        while (this.gridT + step <= t) {
            this.gridT += step;
            this.gridPos = (this.gridPos + 1) % len;
            this.grid[this.gridPos] = 0;
            if (this.gridFill < len) this.gridFill++;
        }
        if (v > this.grid[this.gridPos]) this.grid[this.gridPos] = v;
    };

    Tracker.prototype.gridAt = function (back) {
        var len = this.grid.length;
        return this.grid[(this.gridPos - back + len * 4) % len];
    };

    Tracker.prototype.estimateTempo = function (t) {
        var n = this.gridFill, hz = P.GRID_HZ;
        if (n < hz * 4) { this.conf = 0; return; }
        var i, L;
        /* mean-removed onset curve, newest last */
        var x = new Float64Array(n), mean = 0;
        for (i = 0; i < n; i++) { x[i] = this.gridAt(n - 1 - i); mean += x[i]; }
        mean /= n;
        var e0 = 0;
        for (i = 0; i < n; i++) { x[i] -= mean; e0 += x[i] * x[i]; }
        if (e0 <= 1e-9) { this.conf = 0; return; }
        var lmin = Math.floor(hz * 60 / P.BPM_MAX), lmax = Math.ceil(hz * 60 / P.BPM_MIN);
        var ac = new Float64Array(4 * lmax + 2);
        for (L = lmin; L <= Math.min(4 * lmax + 1, n - 1); L++) {
            var s = 0;
            for (i = L; i < n; i++) s += x[i] * x[i - L];
            ac[L] = s / e0;
        }
        var best = -1, bestL = 0;
        for (L = lmin; L <= lmax; L++) {
            var bpm = 60 * hz / L;
            var oct = Math.log(bpm / P.BPM_PRIOR) / Math.LN2 / P.BPM_PRIOR_OCT;
            var prior = Math.exp(-0.5 * oct * oct);
            /* the period and its double both line up for the true beat */
            /* the true beat lines up at 1, 2 and 4 beats (a bar); 2/3 or 4/3 of it does not */
            var sc = (ac[L] + 0.5 * (2 * L < ac.length ? ac[2 * L] : 0) + 0.25 * (4 * L < ac.length ? ac[4 * L] : 0)) * prior;
            if (sc > best) { best = sc; bestL = L; }
        }
        if (bestL <= lmin || bestL >= lmax) { this.conf *= 0.5; this.lockEvidence = 0; return; }
        /* parabolic refinement of the lag */
        var y0 = ac[bestL - 1], y1 = ac[bestL], y2 = ac[bestL + 1];
        var den = y0 - 2 * y1 + y2, off = den < 0 ? 0.5 * (y0 - y2) / den : 0;
        var lag = bestL + Math.max(-0.5, Math.min(0.5, off));
        var newBpm = 60 * hz / lag;
        var conf = Math.max(0, ac[bestL]);
        this.conf = conf;
        var steady = this.lastEstBpm > 0 && Math.abs(newBpm - this.lastEstBpm) / this.lastEstBpm < 0.04;
        this.lastEstBpm = newBpm;
        if (conf >= P.LOCK_CONF && steady) this.lockEvidence++;
        else if (conf < P.UNLOCK_CONF || !steady) this.lockEvidence = 0;
        if (conf < P.UNLOCK_CONF * 0.5) { this.pendingN = 0; return; }
        if (this.bpm > 0 && Math.abs(newBpm - this.bpm) / this.bpm < 0.06) {
            this.bpm += (newBpm - this.bpm) * 0.25;
            this.pendingN = 0;
        } else if (this.pendingBpm > 0 && Math.abs(newBpm - this.pendingBpm) / this.pendingBpm < 0.06) {
            if (++this.pendingN >= (this.bpm > 0 ? 3 : 1)) { this.bpm = newBpm; this.pendingN = 0; }
        } else {
            this.pendingBpm = newBpm; this.pendingN = 1;
            if (this.bpm === 0) this.bpm = newBpm;
        }
        this.period = 60 / this.bpm;
        this.estimatePhase(t);
    };

    /* Phase: the offset within one period where the onset curve of the last
       few periods piles up (recent periods weigh more). */
    Tracker.prototype.estimatePhase = function (t) {
        var hz = P.GRID_HZ, per = this.period * hz, n = this.gridFill;
        if (!(per > 2)) return;
        var nper = Math.min(6, Math.floor((n - 2) / per));
        if (nper < 2) return;
        var steps = Math.max(8, Math.round(per)), bestS = -1, bestPh = 0;
        for (var j = 0; j < steps; j++) {
            var ph = j * per / steps, s = 0, w = 1;
            for (var q = 0; q < nper; q++) {
                var back = Math.round(ph + q * per);
                var v = Math.max(this.gridAt(back), this.gridAt(back + 1), back > 0 ? this.gridAt(back - 1) : 0);
                s += v * w; w *= 0.8;
            }
            if (s > bestS) { bestS = s; bestPh = ph; }
        }
        var lastBeatT = this.gridT - bestPh / hz;
        var predicted = lastBeatT;
        while (predicted <= t) predicted += this.period;
        if (this.nextBeat <= 0 || !this.locked) { this.nextBeat = predicted; return; }
        var e = predicted - this.nextBeat;
        e -= Math.round(e / this.period) * this.period;
        if (Math.abs(e) > 0.25 * this.period) {
            this.phaseMiss = (this.phaseMiss || 0) + 1;
            if (this.phaseMiss >= 2) { this.nextBeat += e; this.phaseMiss = 0; }
        } else {
            this.nextBeat += 0.5 * e;
            this.phaseMiss = 0;
        }
    };

    Tracker.prototype.advanceBeat = function (t, presence) {
        this.beatEvent = 0;
        var gap = Math.max(P.LOCK_GAP_MIN_S, P.LOCK_GAP_BEATS * (this.period || 0.5));
        var lastOn = Math.max(this.lastOnset[0], this.lastOnset[1], this.lastOnset[2]);
        var alive = presence > 0.3 && t - lastOn < gap;
        if (!alive) this.lockEvidence = 0;
        var wasLocked = this.locked;
        this.locked = alive && this.bpm > 0 &&
            (wasLocked ? this.conf >= P.UNLOCK_CONF : this.lockEvidence >= P.LOCK_EVIDENCE);
        if (!alive && t - lastOn > gap * 2) {
            /* the music stopped: forget the tempo, the next song brings its own */
            this.bpm = 0; this.period = 0; this.pendingN = 0; this.conf = 0;
            this.locked = false;
        }
        if (!this.locked) return;
        if (!wasLocked && this.nextBeat < t) {
            while (this.nextBeat < t) this.nextBeat += this.period;
        }
        if (t >= this.nextBeat) {
            this.beatEvent = 1;
            this.lastBeat = this.nextBeat;
            this.nextBeat += this.period;
            if (this.nextBeat < t) this.nextBeat = t + this.period;
        }
    };

    return {
        BANDS: BANDS,
        GROUPS: GROUPS,
        GROUP_EDGES: GROUP_EDGES,
        FRAME_LEN: FRAME_LEN,
        PARAMS: P,
        RealFFT: RealFFT,
        Analyzer: Analyzer,
        Tracker: Tracker,
        bandEdges: bandEdges
    };
});
